import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Building, Mail, Phone, Globe, MapPin, Edit, Trash2 } from 'lucide-react';
import { useVendors, useDeleteVendor } from '@/hooks/useVendors';
import { VendorForm } from '@/components/vendors/VendorForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getCategoryColor, VENDOR_CATEGORIES, VendorCategoryType } from '@/types/vendor';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const Vendors: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const { data: vendors, isLoading } = useVendors();
  const deleteVendor = useDeleteVendor();
  const { toast } = useToast();

  const filteredVendors = vendors?.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || vendor.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const totalVendors = vendors?.length || 0;
  const uniqueCategories = [...new Set(vendors?.map(v => v.category) || [])].length;
  const activeVendors = vendors?.length || 0;

  const handleEdit = (vendor: any) => {
    setSelectedVendor(vendor);
    setShowEditDialog(true);
  };

  const handleDelete = async (vendor: any) => {
    if (window.confirm(`Are you sure you want to delete ${vendor.name}?`)) {
      try {
        await deleteVendor.mutateAsync(vendor.id);
        toast({
          title: "Success",
          description: "Vendor deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete vendor",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="fullscreen" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Vendors Management</h1>
            <p className="text-gray-600 mt-1">Manage your global vendor database</p>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)} 
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Vendors</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{totalVendors}</p>
                  <p className="text-sm text-gray-500 mt-1">Registered vendors</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                  <Building className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Categories</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{uniqueCategories}</p>
                  <p className="text-sm text-gray-500 mt-1">Unique categories</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Vendors</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{activeVendors}</p>
                  <p className="text-sm text-gray-500 mt-1">Available for events</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 border-0 bg-gray-50 rounded-xl focus:bg-white focus:shadow-md transition-all duration-200"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-48 border-0 bg-gray-50 rounded-xl focus:bg-white focus:shadow-md transition-all duration-200">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {VENDOR_CATEGORIES.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Vendors List */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold">
              Vendors ({filteredVendors?.length || 0})
            </CardTitle>
            <p className="text-gray-600">Manage your vendor database</p>
          </CardHeader>
          <CardContent className="p-0">
            {filteredVendors?.length === 0 ? (
              <div className="text-center py-12">
                <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first vendor'}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-hidden">
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 text-sm font-medium text-gray-700 border-b">
                  <div className="col-span-3">Vendor Name</div>
                  <div className="col-span-1">Category</div>
                  <div className="col-span-2">Contact Person</div>
                  <div className="col-span-2">Contact Info</div>
                  <div className="col-span-2">Location</div>
                  <div className="col-span-1">Tags</div>
                  <div className="col-span-1">Actions</div>
                </div>

                {/* Vendor Rows */}
                {filteredVendors?.map((vendor, index) => (
                  <div 
                    key={vendor.id} 
                    className={`grid grid-cols-12 gap-4 px-6 py-6 border-b hover:bg-gray-50/50 transition-colors duration-150 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                    }`}
                  >
                    {/* Vendor Name */}
                    <div className="col-span-3">
                      <div className="font-semibold text-gray-900">{vendor.name}</div>
                      {vendor.description && (
                        <div className="text-sm text-gray-500 mt-1 line-clamp-1">{vendor.description}</div>
                      )}
                    </div>

                    {/* Category */}
                    <div className="col-span-1">
                      <Badge className={`${getCategoryColor(vendor.category)} border rounded-full px-3 py-1 text-xs font-medium`}>
                        {vendor.category}
                      </Badge>
                    </div>

                    {/* Contact Person */}
                    <div className="col-span-2">
                      <div className="text-gray-900">{vendor.contact_person || '-'}</div>
                    </div>

                    {/* Contact Info */}
                    <div className="col-span-2 space-y-1">
                      {vendor.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="w-3 h-3 mr-1" />
                          {vendor.phone}
                        </div>
                      )}
                      {vendor.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-3 h-3 mr-1" />
                          {vendor.email}
                        </div>
                      )}
                      {vendor.website && (
                        <div className="flex items-center text-sm">
                          <Globe className="w-3 h-3 mr-1 text-gray-400" />
                          <a 
                            href={vendor.website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-600 hover:underline"
                          >
                            Website
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Location */}
                    <div className="col-span-2">
                      {vendor.address && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-3 h-3 mr-1" />
                          <span className="truncate">{vendor.address}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="col-span-1">
                      {vendor.tags && vendor.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {vendor.tags.slice(0, 2).map((tag, tagIndex) => (
                            <Badge 
                              key={tagIndex} 
                              variant="outline" 
                              className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border-gray-200"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {vendor.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border-gray-200">
                              +{vendor.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="col-span-1">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(vendor)}
                          className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(vendor)}
                          className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Vendor Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Add New Vendor</DialogTitle>
            </DialogHeader>
            <VendorForm
              onSuccess={() => setShowCreateDialog(false)}
              onCancel={() => setShowCreateDialog(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Edit Vendor Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Edit Vendor</DialogTitle>
            </DialogHeader>
            <VendorForm
              vendor={selectedVendor}
              onSuccess={() => {
                setShowEditDialog(false);
                setSelectedVendor(null);
              }}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedVendor(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Vendors;
